import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Collapse,
} from "@mui/material";
import callApi from "../../services/callApi";
import { useNavigate } from "react-router-dom";
import HeaderClientes from "./components/HeaderClientes"; // MODIFICADO: Nuevo header flotante
import FooterCliente from "./components/FooterCliente"; // AGREGADO: Import del footer
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";


const HomeCliente = () => {
  const [searchOptions, setSearchOptions] = useState({
    n_profesionales: 0,
    especialidades: [],
    comunas: [],
  });
  const [inputProfession, setInputProfession] = useState("");
  const [inputComuna, setInputComuna] = useState("");
  const [selectedProfession, setSelectedProfession] = useState("");
  const [selectedComuna, setSelectedComuna] = useState("");
  const [serviceType, setServiceType] = useState("online");
  const navigate = useNavigate();

  // Estado para controlar la animación de transición
  const showComunaField =
    serviceType === "presencial" || serviceType === "domicilio";

  useEffect(() => {
    const fetchSearchOptions = async () => {
      try {
        const response = await callApi("/pacientes/buscadorOpciones");
        setSearchOptions({
          n_profesionales: response.n_profesionales,
          especialidades: response.especialidades,
          comunas: response.comunas,
        });
      } catch (error) {
        console.error("Error al obtener opciones de búsqueda:", error);
      }
    };

    fetchSearchOptions();
  }, []);

  const handleServiceTypeChange = (type) => {
    setServiceType(type);

    // Resetear la comuna seleccionada si se cambia a online
    if (type === "online") {
      setSelectedComuna("");
      setInputComuna("");
    }
  };

  const normalizeString = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const filterOptions = (options, { inputValue }) => {
    const normalizedInput = normalizeString(inputValue);
    return options.filter((option) => {
      const normalizedOption = normalizeString(option);
      return normalizedOption.includes(normalizedInput);
    });
  };

  const filterComunaOptions = (options, { inputValue }) => {
    const normalizedInput = normalizeString(inputValue);
    return options.filter((option) => {
      const comunaName = option.split(",")[0];
      const normalizedOption = normalizeString(comunaName);
      return normalizedOption.includes(normalizedInput);
    });
  };

  const isSearchEnabled = () => {
    if (serviceType === "online") {
      return selectedProfession;
    }
    return serviceType && selectedProfession && selectedComuna; // Requiere todos los campos para presencial y domicilio
  };

  const handleSearch = () => {
    const searchParams = {
      modalidad: serviceType || "",
      especialidad: selectedProfession || "",
      comuna: selectedComuna || "",
    };

    navigate(
      {
        pathname: "/busqueda",
        search: `?m=${encodeURIComponent(
          searchParams.modalidad
        )}&e=${encodeURIComponent(
          searchParams.especialidad
        )}&c=${encodeURIComponent(searchParams.comuna)}`,
      },
      {
        state: searchParams,
      }
    );
  };

  const pageTitle = "Clinifica";
  const pageDescription = "Elige tu servicio: Recupera tu bienestar.";

  return (
    <Box
      sx={{
        minHeight: "100vh", // Altura mínima de viewport completo
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      <HeaderClientes /> {/* AGREGADO: Nuevo header flotante */}
      <Container 
        maxWidth="lg"
        sx={{ flex: 1 }} // Crece para empujar el footer abajo
      >
        <Box
          sx={{
            backgroundColor: "#4669E8",
            borderRadius: "8px",
            p: { xs: 3, md: 6 },
            mb: 8,
            mt: { xs: "200px", md: "225px" }, // MODIFICADO: Aumentado para mantener distancia visual original
          }}
        >
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "24px", md: "32px" },
              color: "white",
              mb: 1,
              whiteSpace: { xs: "pre-line", md: "normal" },
            }}
          >
            {isMobile
              ? "Encuentra tu\nprofesional de la salud"
              : "Encuentra tu profesional de la salud"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "white",
              mb: 3,
            }}
          >
            {`+${searchOptions.n_profesionales} profesionales están aquí para ayudarte`}
          </Typography>

          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              p: 2,
            }}
          >
            {/* Botones de tipo de servicio */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
              }}
            >
              {["online", "presencial", "domicilio"].map((type) => (
                <Button
                  key={type}
                  onClick={() => handleServiceTypeChange(type)}
                  sx={{
                    backgroundColor:
                      serviceType === type
                        ? "white"
                        : "rgba(255, 255, 255, 0.1)",
                    color: serviceType === type ? "black" : "white",
                    textTransform: "none",
                    fontFamily: "Quicksand-Regular",
                    "&:hover": {
                      backgroundColor:
                        serviceType === type
                          ? "white"
                          : "rgba(255, 255, 255, 0.2)",
                    },
                    px: 3,
                  }}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              ))}
            </Box>

            {/* Contenedor de los campos de búsqueda con aplicación condicional diferenciada para móvil/desktop */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Contenedor para los campos */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexGrow: 1,
                      marginRight: 2,
                      position: "relative",
                    }}
                  >
                    {/* Campo de Profesión con transición de ancho */}
                    <Box
                      sx={{
                        width: showComunaField ? "calc(50% - 8px)" : "100%", // -8px compensa el margen entre campos
                        transition: "width 0.3s ease-out",
                        position: "relative",
                      }}
                    >
                      <Autocomplete
                        value={selectedProfession}
                        onChange={(event, newValue) => {
                          setSelectedProfession(newValue);
                        }}
                        inputValue={inputProfession}
                        onInputChange={(event, newInputValue) => {
                          setInputProfession(newInputValue);
                        }}
                        options={searchOptions.especialidades}
                        filterOptions={filterOptions}
                        freeSolo
                        noOptionsText="No se encontraron resultados"
                        sx={{
                          width: "100%",
                          backgroundColor: "white",
                          borderRadius: 1,
                          "& .MuiOutlinedInput-root": {
                            py: 0,
                            "& input": {
                              py: 1.5,
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#111827",
                          },
                          "& .MuiInputBase-input::placeholder": {
                            color: "#9CA3AF",
                            opacity: 1,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Profesión o especialidad"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "transparent",
                                },
                                "&:hover fieldset": {
                                  borderColor: "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "transparent",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>

                    {/* Campo de Comuna con aparición animada */}
                    <Box
                      sx={{
                        width: showComunaField ? "calc(50% - 8px)" : "0%", // -8px compensa el margen entre campos
                        opacity: showComunaField ? 1 : 0,
                        transition: "all 0.3s ease-out",
                        position: "absolute",
                        right: 0,
                        height: "100%",
                        overflow: "hidden",
                        pointerEvents: showComunaField ? "auto" : "none",
                      }}
                    >
                      <Autocomplete
                        value={selectedComuna}
                        onChange={(event, newValue) => {
                          setSelectedComuna(newValue);
                        }}
                        inputValue={inputComuna}
                        onInputChange={(event, newInputValue) => {
                          setInputComuna(newInputValue);
                        }}
                        options={searchOptions.comunas}
                        filterOptions={filterComunaOptions}
                        freeSolo
                        noOptionsText="No se encontraron resultados"
                        sx={{
                          width: "100%",
                          backgroundColor: "white",
                          borderRadius: 1,
                          "& .MuiOutlinedInput-root": {
                            py: 0,
                            "& input": {
                              py: 1.5,
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#111827",
                          },
                          "& .MuiInputBase-input::placeholder": {
                            color: "#9CA3AF",
                            opacity: 1,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Ciudad o comuna"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "transparent",
                                },
                                "&:hover fieldset": {
                                  borderColor: "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "transparent",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  {/* Botón Buscar */}
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!isSearchEnabled()}
                    sx={{
                      backgroundColor: isSearchEnabled()
                        ? "#10B981"
                        : "#E5E7EB",
                      fontFamily: "Quicksand-Bold",
                      textTransform: "none",
                      minWidth: "120px",
                      alignSelf: "stretch",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: isSearchEnabled()
                          ? "#059669"
                          : "#E5E7EB",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#E5E7EB",
                        color: "#9CA3AF",
                      },
                    }}
                  >
                    Buscar
                  </Button>
                </Box>
              )}
              {/* Versión móvil con espaciado correcto para modo online */}
              {isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Campo de Profesión */}
                  <Autocomplete
                    value={selectedProfession}
                    onChange={(event, newValue) => {
                      setSelectedProfession(newValue);
                    }}
                    inputValue={inputProfession}
                    onInputChange={(event, newInputValue) => {
                      setInputProfession(newInputValue);
                    }}
                    options={searchOptions.especialidades}
                    filterOptions={filterOptions}
                    freeSolo
                    noOptionsText="No se encontraron resultados"
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: showComunaField ? 2 : 2, // 8px cuando hay comuna, 4px en online
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "& input": {
                          py: 1.5,
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#111827",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#9CA3AF",
                        opacity: 1,
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Profesión o especialidad"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "transparent",
                            },
                            "&:hover fieldset": {
                              borderColor: "transparent",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "transparent",
                            },
                          },
                        }}
                      />
                    )}
                  />

                  {/* Campo de Comuna con Collapse para mantener el efecto original */}
                  <Collapse
                    in={showComunaField}
                    timeout={300}
                    sx={{
                      mb: showComunaField ? 2 : 0, // 8px cuando es visible, 0 cuando no
                      mt: 0, // Sin compensación negativa para evitar problemas
                      height: showComunaField ? "auto" : 0, // Asegura que no ocupe espacio cuando no es visible
                      overflow: "hidden",
                    }}
                  >
                    <Autocomplete
                      value={selectedComuna}
                      onChange={(event, newValue) => {
                        setSelectedComuna(newValue);
                      }}
                      inputValue={inputComuna}
                      onInputChange={(event, newInputValue) => {
                        setInputComuna(newInputValue);
                      }}
                      options={searchOptions.comunas}
                      filterOptions={filterComunaOptions}
                      freeSolo
                      noOptionsText="No se encontraron resultados"
                      sx={{
                        width: "100%",
                        backgroundColor: "white",
                        borderRadius: 1,
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "& input": {
                            py: 1.5,
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#111827",
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Ciudad o comuna"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "transparent",
                              },
                              "&:hover fieldset": {
                                borderColor: "transparent",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "transparent",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Collapse>

                  {/* Botón Buscar */}
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!isSearchEnabled()}
                    sx={{
                      backgroundColor: isSearchEnabled()
                        ? "#10B981"
                        : "#E5E7EB",
                      fontFamily: "Quicksand-Bold",
                      textTransform: "none",
                      // Sin margen adicional
                      "&:hover": {
                        backgroundColor: isSearchEnabled()
                          ? "#059669"
                          : "#E5E7EB",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#E5E7EB",
                        color: "#9CA3AF",
                      },
                    }}
                  >
                    Buscar
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Features Section */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 4,
            mb: 8,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "18px" },
                color: "#111827",
                mb: 1,
              }}
            >
              Encuentra tu especialista
            </Typography>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#6B7280",
                lineHeight: 1.5,
              }}
            >
              Trabajamos solo con profesionales autorizados por la
              superintendencia de salud.
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "18px" },
                color: "#111827",
                mb: 1,
              }}
            >
              Pide tu cita de forma fácil
            </Typography>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#6B7280",
                lineHeight: 1.5,
              }}
            >
              Elige el servicio que prefieras y pide reserva tu hora de forma
              fácil sin tener que llamar.
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "18px" },
                color: "#111827",
                mb: 1,
              }}
            >
              Recordatorios por WhatsApp
            </Typography>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#6B7280",
                lineHeight: 1.5,
              }}
            >
              Te confirmamos la cita al instante por email y te enviaremos
              varios recordatorios a tu WhatsApp antes de la cita.
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "18px" },
                color: "#111827",
                mb: 1,
              }}
            >
              Programa tu seguimiento
            </Typography>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#6B7280",
                lineHeight: 1.5,
              }}
            >
              Agenda fácilmente tus próximas citas y mantén el control de tus
              tratamientos directamente con tu profesional.
            </Typography>
          </Box>
        </Box>
      </Container>
      <Box sx={{ overflowX: "hidden" }}>
        <FooterCliente />
      </Box>
    </Box>
  );
};

export default HomeCliente;
